import { maxLength, required } from '@seek/validators-js';
import { useTranslations } from '@vocab/react';
import { IconWorkExperience, TextField, Text } from 'braid-design-system';
import debounce from 'lodash.debounce';
import {
  type ChangeEvent,
  type Ref,
  useState,
  forwardRef,
  useImperativeHandle,
  useCallback,
  useMemo,
  useEffect,
} from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { v4 as uid } from 'uuid';

import {
  JOB_TITLE_PRESSED,
  TOOLTIP_PRESSED,
} from 'src/app/shared/dataAnalytics/schema/constants';
import {
  FEATURE_FILTER_CONTENT,
  useFeatureToggleData,
} from 'src/app/shared/featureToggle';
import { useConfig } from 'src/app/shared/hooks/useConfig';
import {
  type Validators,
  filterContent,
  validateField,
} from 'src/app/utils/validation';
import getTrackingAdapter from 'src/app/v1/dataAnalytics';
import { sendContentSafetyTrackingEvents } from 'src/app/v1/dataAnalytics/utils/sendContentSafetyTrackingEvents';
import { actionUpdateJobTitle } from 'src/app/v1/state/modules/formData/jobTitle';
import { actionUpdateInteractionId } from 'src/app/v1/state/modules/session/interactionId';
import { actionUpdateRoleTitle } from 'src/app/v1/state/modules/tealium/roleTitle';
import { useDraft } from 'src/app/v1/views/hooks/useDraft';
import { ContextualHelp } from 'src/app/v1/views/shared/ContextualHelp';
import type { AppState } from 'src/types/appState';
import type { JobTitleRef } from 'src/types/formFields';

import translations from './.vocab';

interface JobTitleProps {
  hideHelp?: boolean;
  disabled?: boolean;
}

export const JobTitle = forwardRef(
  ({ hideHelp, disabled }: JobTitleProps, ref: Ref<JobTitleRef>) => {
    const { t } = useTranslations(translations);
    const language = useConfig().getLanguage();

    const { draftId, flowType, jobTitle, jobLocationCountry, hirerNation } =
      useSelector(
        (state: AppState) => ({
          draftId: state.session.draftId,
          flowType: state.formData.flowType,
          jobTitle: state.formData.jobTitle,
          jobLocationCountry: state.session.jobLocationCountry,
          hirerNation: state.session?.hirerAccount?.nation,
        }),
        shallowEqual,
      );

    const [hasInteracted, setHasInteracted] = useState<boolean>(false);

    const [validationMessage, setValidationMessage] = useState<string | null>(
      null,
    );
    const [inputValue, setInputValue] = useState<string>(jobTitle);
    const country = (jobLocationCountry ?? hirerNation ?? 'AU').toLowerCase();
    const isFilterContentEnabled = useFeatureToggleData(FEATURE_FILTER_CONTENT);

    const dispatch = useDispatch();
    const { createDraft } = useDraft();

    const labelName = t('job-title-field-label');

    const handleInputValidation = useCallback(
      (
        value: string,
        skipIfNull = false,
        checkRequired = true,
      ): string | null => {
        let validators: Validators = [maxLength];
        if (checkRequired) validators = [...validators, required];
        if (isFilterContentEnabled) validators = [...validators, filterContent];

        const validationResult = validateField(
          {
            field: 'jobTitle',
            validators,
            validatorProps: {
              max: 80,
              requiredLabel: labelName.toLowerCase(),
              country,
              sendTrackingEvents: sendContentSafetyTrackingEvents,
            },
            value,
          },
          skipIfNull,
          language,
        );

        setValidationMessage(validationResult);

        return validationResult;
      },
      [labelName, country, isFilterContentEnabled, language],
    );

    const handleUpdate = useCallback(
      (value: string) => {
        const errorMessage = handleInputValidation(value);

        if (!errorMessage) {
          dispatch(actionUpdateJobTitle(value));
          dispatch(actionUpdateRoleTitle(value)); // tealium

          dispatch(actionUpdateInteractionId(uid()));

          if (flowType === 'create' && !draftId) {
            createDraft();
          }
        }
      },
      [draftId, flowType, createDraft, dispatch, handleInputValidation],
    );

    useEffect(() => {
      if (jobLocationCountry && hasInteracted) {
        handleUpdate(inputValue);
      }
      // Only monitor jobLocationCountry value change from Job Location field
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobLocationCountry]);

    // For displaying error message immediately
    // while updating store from onChange is delayed/debounced by x sec
    useEffect(() => {
      handleInputValidation(inputValue, false, hasInteracted);
    }, [
      isFilterContentEnabled,
      inputValue,
      handleInputValidation,
      hasInteracted,
    ]);

    useImperativeHandle(
      ref,
      () => ({
        handleValidation: (skipIfNull = false) =>
          handleInputValidation(inputValue, skipIfNull),
      }),
      [inputValue, handleInputValidation],
    );

    const debouncedOnChange = useMemo(
      () => debounce(handleUpdate, 2000),
      [handleUpdate],
    );

    const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      setHasInteracted(true);
      setInputValue(value);
      debouncedOnChange(value);
    };

    const handleOnBlur = (event: ChangeEvent<HTMLInputElement>) => {
      const eventData = {
        eventName: JOB_TITLE_PRESSED,
        objectInteraction: 'job-title',
      };
      getTrackingAdapter.sendLinkEvent(eventData);

      const { value } = event.target;
      setHasInteracted(true);
      setInputValue(value);
      debouncedOnChange.flush();
    };

    return (
      <TextField
        id="JobTitleTextField"
        icon={<IconWorkExperience />}
        label={
          <>
            {labelName}{' '}
            {!hideHelp && (
              <ContextualHelp
                ariaLabel="Job Title Help"
                tooltipId="job-title-help-tooltip"
                message={<Text>{t('job-title-contextual-help-text')}</Text>}
                onTrigger={() =>
                  getTrackingAdapter.sendLinkEvent({
                    eventName: TOOLTIP_PRESSED,
                    toolTipType: 'job title help',
                  })
                }
              />
            )}
          </>
        }
        message={validationMessage}
        onBlur={handleOnBlur}
        onChange={handleOnChange}
        value={inputValue}
        tone={validationMessage ? 'critical' : 'neutral'}
        data={{ 'hj-whitelist': 'true' }}
        reserveMessageSpace
        disabled={disabled}
        placeholder={t('job-title-field-placeholder')}
      />
    );
  },
);
